import React from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Paper
} from '@mui/material';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem2 } from '@mui/x-tree-view/TreeItem2';
import {
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  UnfoldMore as ExpandAllIcon,
  UnfoldLess as CollapseAllIcon
} from '@mui/icons-material';
import TreeSearch from './TreeSearch';
import TreeNode from './TreeNode';
import useTreeState from './useTreeState';

/**
 * Generic Hierarchical Tree Selector Component
 *
 * Supports multiple modes:
 * - View-only mode (Permission Management)
 * - Checkbox selection mode (Role Permission Assignment)
 *
 * Features:
 * - Cascade selection with deduplication
 * - Search and filter
 * - Expand/collapse controls
 * - Custom node rendering
 * - Optimized for 1000+ nodes
 */
const HierarchicalTreeSelector = ({
  // Data
  treeData = [],
  selectedItems = [],

  // Callbacks
  onSelectionChange,
  onNodeClick,
  onNodeExpand,

  // Selection Mode
  selectionMode = 'checkbox',        // checkbox | none | view-only
  multiSelect = true,
  cascadeSelection = true,
  deduplicateOnCascade = true,

  // Search & Filter
  searchEnabled = true,
  searchPlaceholder = 'Search...',
  onSearch,

  // Display Options
  showSelectedCount = true,
  showNodeIcons = true,
  showNodeActions = false,
  expandedByDefault = false,
  expandAllButton = true,
  collapseAllButton = true,

  // Node Rendering
  getNodeId = (node) => node.id,
  getNodeLabel = (node) => node.name,
  getNodeChildren = (node) => node.children,
  getNodeIcon,
  renderNodeLabel,
  renderNodeActions,

  // Styling
  height = 400,
  variant = 'outlined',

  // State
  disabled = false,
  loading = false,
  error = null
}) => {
  // Use tree state hook
  const {
    selectedItems: internalSelected,
    expandedNodes,
    searchQuery,
    filteredTree,
    toggleNode,
    clearSelection,
    isNodeChecked,
    isNodeIndeterminate,
    expandAll,
    collapseAll,
    setExpandedNodes,
    setSearchQuery
  } = useTreeState({
    treeData,
    initialSelected: selectedItems,
    initialExpanded: expandedByDefault
      ? treeData.map(node => getNodeId(node))
      : [],
    cascadeSelection,
    deduplicateOnCascade,
    onSelectionChange,
    getNodeId
  });

  // Handle search
  const handleSearch = (query) => {
    setSearchQuery(query);
    if (onSearch) {
      onSearch(query);
    }
  };

  // Handle node toggle
  const handleNodeToggle = (node) => {
    if (disabled || selectionMode === 'view-only') return;
    const nodeId = getNodeId(node);
    toggleNode(nodeId);
  };

  // Handle node click
  const handleNodeClick = (node) => {
    if (onNodeClick) {
      console.log('node clicked');
      onNodeClick(node);
    }
  };

  // Render tree recursively
  const renderTree = (nodes) => {
    if (!nodes || nodes.length === 0) return null;

    return nodes.map((node) => {
      const nodeId = getNodeId(node);

      // Skip nodes without valid IDs
      if (!nodeId) {
        console.warn('Node missing ID:', node);
        return null;
      }

      const checked = isNodeChecked(nodeId);
      const indeterminate = isNodeIndeterminate(nodeId);
      const children = getNodeChildren(node);

      return (
        <TreeItem2
          key={nodeId}
          itemId={nodeId}
          label={
            <TreeNode
              node={node}
              checked={checked}
              indeterminate={indeterminate}
              selectionMode={selectionMode}
              onToggle={handleNodeToggle}
              renderLabel={renderNodeLabel}
              renderIcon={showNodeIcons ? getNodeIcon : null}
              renderActions={renderNodeActions}
              showNodeActions={showNodeActions}
              disabled={disabled}
            />
          }
          onClick={() => handleNodeClick(node)}
        >
          {children && children.length > 0 ? renderTree(children) : null}
        </TreeItem2>
      );
    }).filter(Boolean); // Remove null entries
  };

  // Selected count
  const selectedCount = internalSelected.length;

  return (
    <Box>
      {/* Search */}
      {searchEnabled && (
        <TreeSearch
          value={searchQuery}
          onChange={handleSearch}
          placeholder={searchPlaceholder}
          disabled={disabled || loading}
        />
      )}

      {/* Action Buttons */}
      {(expandAllButton || collapseAllButton || (selectedCount > 0 && multiSelect && selectionMode === 'checkbox')) && (
        <Box display="flex" gap={1} mb={2} flexWrap="wrap">
          {expandAllButton && (
            <Button
              size="small"
              startIcon={<ExpandAllIcon />}
              onClick={expandAll}
              disabled={disabled || loading}
            >
              Expand All
            </Button>
          )}
          {collapseAllButton && (
            <Button
              size="small"
              startIcon={<CollapseAllIcon />}
              onClick={collapseAll}
              disabled={disabled || loading}
            >
              Collapse All
            </Button>
          )}
          {selectedCount > 0 && multiSelect && selectionMode === 'checkbox' && (
            <Button
              size="small"
              onClick={clearSelection}
              disabled={disabled || loading}
              sx={{ ml: 'auto' }}
            >
              Clear Selection
            </Button>
          )}
        </Box>
      )}

      {/* Selected Count Alert */}
      {showSelectedCount && selectedCount > 0 && selectionMode === 'checkbox' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {selectedCount} item{selectedCount !== 1 ? 's' : ''} selected
        </Alert>
      )}

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      )}

      {/* Tree View */}
      {!loading && filteredTree.length > 0 ? (
        <Paper
          variant={variant}
          sx={{
            maxHeight: height,
            overflowY: 'auto',
            p: 2
          }}
        >
          <SimpleTreeView
            slots={{
              collapseIcon: ExpandMoreIcon,
              expandIcon: ChevronRightIcon,
            }}
            expandedItems={expandedNodes}
            onExpandedItemsChange={(event, itemIds) => {
              setExpandedNodes(itemIds);
              if (onNodeExpand) {
                // Find which node was toggled
                const diff = itemIds.filter(id => !expandedNodes.includes(id));
                if (diff.length > 0) {
                  onNodeExpand(diff[0]);
                }
              }
            }}
            sx={{ flexGrow: 1 }}
          >
            {renderTree(filteredTree)}
          </SimpleTreeView>
        </Paper>
      ) : !loading && (
        <Typography color="text.secondary" textAlign="center" py={4}>
          {searchQuery ? 'No items found' : 'No items to display'}
        </Typography>
      )}
    </Box>
  );
};

export default HierarchicalTreeSelector;
